import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  resource,
  signal,
  untracked,
} from '@angular/core';
import { ClarityModule } from '@clr/angular';
import { ZaehlerApiService } from 'src/app/services/APIService/zaehler.api.service';
import { MessungGroupÜbersicht, MessungÜbersicht, ZählerWert } from '../../_overlays/zaehlerverwaltung/zaehler.types';
import { BackendLocaleDatePipe } from 'src/app/pipes/get-locale-date.pipe';
import { MrTranslatePipe } from 'src/app/pipes/mr-translate.pipe';
import { TablePrettyPrintPipe } from 'src/app/pipes/tablePrettyPrint.pipe';
import { FilterTolByNameOptionsPipe } from '../../_overlays/zaehlerverwaltung/filter-tol-by-options.pipe';
import { TransformColNamesPipe } from '../../_overlays/zaehlerverwaltung/transform-col-names.pipe';
import { AusfallColorPipe } from '../../_tabs/zaehlertab/ausfall-color.pipe';
import { HideIDColumnsPipe } from 'src/app/pipes/hide-idcolumns.pipe';
import { WerteRowValuesPipe } from '../../_overlays/zaehlerverwaltung/werte-row-values.pipe';
import { ZType } from '../../_overlays/zaehlerverwaltung/zaehler.enums';
import { ImageSlideShowComponent } from '../../_shared/media-slide-show/image-slide-show.component';
import { MessungVergleichComponent } from '../../_modals/messung-vergleich/messung-vergleich.component';

@Component({
  selector: 'app-zaehler-accordion',
  templateUrl: './zaehler-accordion.component.html',
  styleUrl: './zaehler-accordion.component.scss',
  imports: [
    CommonModule,
    ClarityModule,
    FilterTolByNameOptionsPipe,
    TransformColNamesPipe,
    AusfallColorPipe,
    MrTranslatePipe,
    TablePrettyPrintPipe,
    HideIDColumnsPipe,
    WerteRowValuesPipe,
    ImageSlideShowComponent,
    MessungVergleichComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ZaehlerAccordionComponent {
  private readonly api = inject(ZaehlerApiService);
  private readonly localeDate = inject(BackendLocaleDatePipe);

  public readonly zaehler = input.required<ZählerConcatenated>();
  private readonly SZAEHNID = computed(() => this.zaehler()?.SZAEHNID);

  protected readonly tol = this.api.getToleranzenBezeichnungen();
  protected readonly selectedRow = signal<MessungÜbersicht>(undefined);

  protected readonly ZType_Header = ZType.Header;

  private readonly pruefungen = computed(() =>
    this.zaehler()
      ?.Pruefungen.split('|')
      .map(x => x.split('='))
      .map(([opruefid, datum]) => ({ OPRUEFID: +opruefid, datum }))
  );

  private readonly lastOPRUEFID = computed(() => this.pruefungen()?.[0].OPRUEFID);

  private readonly messungenResource = resource({
    params: this.pruefungen,
    loader: ({ params }) =>
      Promise.all(
        params.map(pruefung =>
          this.api.getZaehlerMessungByOPRUEFID(pruefung.OPRUEFID).then(messwerte => ({ pruefung, messwerte }))
        )
      ),
  });

  private readonly zaehlerNameRs = this.api.getZaehlerBySZAEHNID(this.SZAEHNID);
  protected readonly zaehlerName = this.zaehlerNameRs.value;
  protected readonly skizzeRs = resource({
    params: () => {
      const opruefid = this.lastOPRUEFID();
      const name = this.zaehlerName()?.Bild;
      if (!name) return undefined;
      return { opruefid, name };
    },
    loader: ({ params: { opruefid, name } }) => this.api.getSkizzeWithAusfallPoints(opruefid, name),
  });

  private readonly zaehlerWerteRs = this.api.getZaehlerWerteBySZAEHNID(this.SZAEHNID);
  protected readonly checklist = this.zaehlerWerteRs.value;

  protected readonly isLoading = computed(
    () =>
      this.zaehlerNameRs.isLoading() ||
      this.skizzeRs.isLoading() ||
      this.zaehlerWerteRs.isLoading() ||
      this.messungenResource.isLoading()
  );

  protected readonly messungen = computed<MessungGroupÜbersicht[]>(() => {
    if (!(this.messungenResource.hasValue() && this.zaehlerWerteRs.hasValue())) return null;
    const messungenGroups = this.messungenResource.value();
    const checklist = this.checklist().rows;

    let groups_mapped: MessungGroupÜbersicht[];

    untracked(() => {
      let lastInd: number, lastDatum: string;
      groups_mapped = messungenGroups.flatMap((group, index) => {
        const { pruefung, messwerte } = group;
        if (!messwerte.length) return [];

        let Column_Datum = this.localeDate.transform(pruefung.datum);

        if (Column_Datum == lastDatum) Column_Datum = `${Column_Datum} (${index - lastInd})`;
        else {
          lastInd = index;
          lastDatum = Column_Datum;
        }

        const formatted_pruefung = { ...pruefung, Column_Datum };
        const matched_messwerte = checklist.map(row => messwerte.find(wert => wert.Bezeichnung == row.Code));
        return [{ pruefung: formatted_pruefung, messwerte: matched_messwerte }];
      });
    });

    return groups_mapped;
  });

  protected findMessungInGroups(row: ZählerWert) {
    const eintrag: MessungÜbersicht = {
      Bezeichnung: row.Code,
      Soll: row.Soll,
      SR100minus: row.SR100minus,
      SR100plus: row.SR100plus,
      SRlimminus: row.SRlimminus,
      SRlimplus: row.SRlimplus,
      SRGminus: row.SRGminus,
      SRGplus: row.SRGplus,
    };

    for (const group of this.messungen()) {
      const date = group.pruefung.Column_Datum;
      const messung = group.messwerte.find(messung => messung?.Bezeichnung == row.Code);
      eintrag[date] = messung?.Ist;
    }

    this.selectedRow.set(eintrag);
  }
}

export interface ZählerConcatenated {
  SZAEHNID: number;
  Code: string;
  Pruefungen: string;
}
